// src/pages/employees/EmployeesPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/common/Breadcrumb';
import CustomDropdown from '@/components/common/CustomDropdown';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import { useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import { getAdminService, deleteAdminService } from '@/services/AdminServices';
import { cn } from '@/lib/utils';
import DataTableFilter from './component/DataTableFilter';
import DataTable from './component/DataTable';
import AddEditDrawer from './component/AddEditDrawer';
import PritntPdfTable from '@/components/common/PritntPdfTable';
import PermissionCheck from '@/components/common/PermissionCheck';

/* Exclude these roles */
const CHEF_ROLE_ID   = '685b1af7956beac77bebe6d0';
const WAITER_ROLE_ID = '685b1af7956beac77bebe6d1';

const EmployeesPage = ({ pageTitle = 'Employees' }) => {
  const dispatch = useDispatch();
  const { printTableRef, handleDownloadPdf } = PritntPdfTable('Items Data');

  const [dataSource, setDataSource]   = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [editDataId, setEditDataId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const filterDivRef = useRef(null);

  // ✅ Just filter out Chef & Waiter (and keep non-super)
  const [dataFilter, setDataFilter] = useState({
    search: '',
    page: 1,
    limit: 10,
    extra_filter: {
      isSuperAdmin: 0,
      role_id: { $nin: [CHEF_ROLE_ID, WAITER_ROLE_ID] },
    },
  });

  const tableColumn = [
    { name: 'Name',          isIncludePrint: true },
    { name: 'Email',         isIncludePrint: true },
    { name: 'Phone',         isIncludePrint: true },
    { name: 'Role',          isIncludePrint: true },
    { name: 'Status',        isIncludePrint: true },
    { name: 'Biometric ID',  isIncludePrint: true },
    { name: 'Salary',        isIncludePrint: true },
    { name: 'Action',        isIncludePrint: false },
  ];

  useEffect(() => { getTableData(); }, [dataFilter]);

  const handleDrawerOpen = (editId) => {
    setEditDataId(editId || '');
    setActiveDrawer(true);
    document.body.style.overflowY = 'hidden';
  };

  // 🔒 Keep the role exclusion locked in
  const onChangeFilter = (name, value) => {
    if (name === 'extra_filter') {
      setDataFilter((p) => ({
        ...p,
        extra_filter: {
          ...p.extra_filter,
          ...value,
          isSuperAdmin: 0,
          role_id: { $nin: [CHEF_ROLE_ID, WAITER_ROLE_ID] },
        },
      }));
    } else {
      setDataFilter((p) => ({ ...p, [name]: value }));
    }
  };

  const handleFilterToggle = () => {
    if (!filterDivRef.current) return;
    setIsActive((prev) => {
      const next = !prev;
      filterDivRef.current.style.height = next ? '100%' : '0px';
      return next;
    });
  };

  const getTableData = () => {
    setIsLoading(true);
    dispatch(getAdminService(dataFilter))
      .then((res) => {
        setTotalRecord(res?.data?.data?.total || 0);
        setDataSource(res?.data?.data?.data || []);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      })
      .finally(() => setIsLoading(false));
  };

  const deleteRecord = (deleteId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        dispatch(deleteAdminService(deleteId))
          .then(() => {
            Swal.fire('Deleted!', 'Your record has been deleted.', 'success');
            getTableData();
          })
          .catch(({ message, errorData }) => {
            validationErrors(errorData, message);
          })
          .finally(() => setIsLoading(false));
      }
    });
  };

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />
      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>
          <div className="db-card-filter">
            <div className="db-field-down-arrow">
              <PerPageSelection onChangeParPage={onChangeFilter} limit={dataFilter.limit} />
            </div>

            <button className="db-card-filter-btn table-filter-btn" onClick={handleFilterToggle}>
              <i className="lab lab-filter lab-font-size-14"></i>
              <span>Filter</span>
            </button>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[
                { icon: 'lab-printer-line', text: 'Print', dropDownAction: handleDownloadPdf },
              ]}
            />

            <PermissionCheck permsission="create_employees">
              <button
                type="button"
                onClick={() => handleDrawerOpen('')}
                className="db-btn h-[37px] text-white bg-primary"
              >
                <i className="lab lab-add-circle-line"></i>
                Add Employees
              </button>
            </PermissionCheck>
          </div>
        </CardHeader>

        <div className={cn('table-filter-div', isActive ? 'active' : '')} ref={filterDivRef}>
          <DataTableFilter onChangeFilter={onChangeFilter} />
        </div>

        <DataTable
          isLoading={isLoading}
          tableColumn={tableColumn}
          dataSource={dataSource}
          totalRecord={totalRecord}
          dataFilter={dataFilter}
          onChangePage={onChangeFilter}
          handleDrawerOpen={handleDrawerOpen}
          deleteRecord={deleteRecord}
        />

        <div ref={printTableRef}>
          <AddEditDrawer
            titleText={pageTitle}
            getTableData={getTableData}
            activeDrawer={activeDrawer}
            setActiveDrawer={setActiveDrawer}
            editDataId={editDataId}
          />
        </div>
      </Card>
    </>
  );
};

export default EmployeesPage;
